import { pushOptimizelyUserAttributes } from 'components/Optimizely/Optimizely.api';
import { OPTIMIZELY_FEATURE_FLAGS_KEYS } from 'components/Optimizely/Optimizely.features';
import { isFeatureEnabledForUser } from 'components/Optimizely/Optimizely.sdk';

/**
 * Configuration for Analytics Data
 *
 * TRAIT_PROPERTIES
 * [-] List of properties that automatically get persisted as user traits when provided to the 'makeFlowTracker' or 'event' method(s).
 *     Useful for properties that we want to associate with all tracking events.
 * [!] NOTE: PII should always be explicitly added via Segment.identify calls.
 */
const TRAIT_PROPERTIES = Object.freeze([
  'business_category',
  'city',
  'country',
  'entity_type',
  'has_legal_entity',
  'has_multiple_locations',
  'special_id',
  'user_type',
]);

/**
 * Configuration for the Segment Interface
 *
 * MAX_RETRIES
 * [-] Max amount of times we re-try finding native Segment on the window object.
 * RETRY_INTERVAL
 * [-] How frequently we poll for native Segment on the window object.
 * METHODS
 * [-] Native Segment methods supported by this interface.
 */
const MAX_RETRIES = 10;
const RETRY_INTERVAL = 500;
const METHODS = Object.freeze(['alias', 'group', 'identify', 'page', 'ready', 'reset', 'track']);

const noop = () => ({});

async function getOptimizelyFlags() {
  try {
    if (!window.CP_USER.user_id) {
      return {};
    }

    const keys = Object.keys(OPTIMIZELY_FEATURE_FLAGS_KEYS);

    const keyAndValuePromises = keys.map(key => {
      const keyOnOptimizely = OPTIMIZELY_FEATURE_FLAGS_KEYS[key];
      const valuePromise = isFeatureEnabledForUser(keyOnOptimizely, window.CP_USER.user_id);

      return valuePromise.then(value => [keyOnOptimizely, value]);
    });

    const keysValues = await Promise.all(keyAndValuePromises);
    return Object.fromEntries(keysValues);
  } catch (error) {
    noop(error);

    return {};
  }
}

async function includeOptimizelyFlags(properties) {
  const optimizelyFlags = await getOptimizelyFlags();

  return { ...properties, ...optimizelyFlags };
}

/**
 * Implementation Below
 */
const eventQueue = new Set();
const queue = (method, ...args) => eventQueue.add([method, args]);
const mapMethods = source => Object.fromEntries(METHODS.map(methodName => [methodName, source(methodName)]));

const configureSegmentIntegrations = () => {
  window.analytics.addSourceMiddleware(({ payload, next }) => {
    payload.obj.integrations = {
      All: true,
      'Sprig (Actions)': !!window.CP_USER?.user_id,
      ...(payload.obj.integrations || {}),
    };

    next(payload);
  });
};

class Segment {
  static hasLoaded() {
    if (
      typeof window !== 'undefined' &&
      // Note: The Segment snippet initializes window.analytics as an array with a subset
      // of properties. This check ensures that the full Segment SDK has loaded (an object).
      // The Segment.ready callback does not work for this - it waits for destinations to load.
      window.analytics &&
      typeof window.analytics.length === 'undefined'
    ) {
      return true;
    }
    return false;
  }

  /**
   * Track a page view for the given location.
   * Safely queues events until window.analytics is available.
   *
   * @param {object} location Location object with pathname and search properties.
   * @returns
   */
  static async makePageTracker(location) {
    // Per instructions from a Segment rep (as this is undocumented):
    // To replicate the behavior of a ga page property and pageview event,
    // we pass along the page data both to the page event itself and as a property
    // page in the event context.
    const pageProps = await includeOptimizelyFlags({ path: location.pathname, search: location.search });

    Segment.page(pageProps, { page: pageProps });
  }

  /**
   * Convenience method for a single call that optionally updates user traits
   * before sending a track event if provided with trait-like event properties (see TRAIT_PROPERTIES).
   *
   * Also handles common idiosyncracies with some of our used destinations.
   *
   * @param {string} eventName Name of the event
   * @param {object} properties Event properties. Can include
   * @param {...any} extra Extra parameters to be forwarded to Segment
   */
  static async event(eventName, properties, ...extra) {
    const traitPropertyEntries = TRAIT_PROPERTIES.filter(key => key in properties).map(key => [key, properties[key]]);

    if (traitPropertyEntries.length) {
      // Workaround for the Optimizely Web <> Segment integration
      // not pushing user traits to the optimizely user context.
      const traitProperties = Object.fromEntries(traitPropertyEntries);
      pushOptimizelyUserAttributes(traitProperties);
      Segment.identify(traitProperties, ...extra);
    }

    return Segment.track(eventName, await includeOptimizelyFlags(properties), ...extra);
  }

  /**
   * Create a flow tracker that lets you easily build events for a flow with multiple steps.
   *
   * @example
   *
   * const flowTracker = makeFlowTracker('Sign-Up');
   *
   * flowTracker.start(); // sends track('User Started Sign-Up')
   * flowTracker.complete(); // sends track('User Completed Sign-Up');
   *
   * const emailStepTracker = flowTracker.step('Email');
   *
   * emailStepTracker.view(); // sends track('Sign-Up Email Step Viewed', { step: 'Email' });
   * emailStepTracker.complete(); // sends track('Sign-Up Email Step Completed', { step: 'Email' });
   * emailStepTracker.error({ error: 'Bad Data' }); // sends track('Sign-Up Email Step Failed', { step: 'Email', error: 'Bad Data' });
   * emailStepTracker.event('Clicked CTA', { source: 'facebook' }); // sends track('Sign-Up Email Clicked CTA', { step: 'Email', source: 'facebook' });
   *
   * const nameStepTracker = flowTracker.step('Name');
   *
   * nameStepTracker.view(); // sends track('Sign-Up Name Step Viewed', { step: 'Name', previous: 'Email' });
   *
   * @param {string} flowName
   * @returns Flow tracking interface with "start", "complete", "step", "step.view", "step.complete", "step.error" and "step.event" methods.
   */
  static makeFlowTracker(flowName) {
    let currentStep;
    const setCurrentStep = stepName => {
      const previousStep = currentStep;
      currentStep = stepName;
      return previousStep;
    };

    const flowEvent =
      eventName =>
      async (properties = {}, ...extra) => {
        // eslint-disable-next-line camelcase
        const { data_layer = {}, ...traitProperties } = properties;
        await Segment.event(`${eventName} ${flowName}`, { category: 'Flow', ...traitProperties }, ...extra);

        // eslint-disable-next-line camelcase
        if (data_layer) {
          // Push flow properties into GTM dataLayer, this will add or replace the data layer variable
          // eslint-disable-next-line camelcase
          (window.dataLayer = window.dataLayer || []).push(data_layer);
        }
      };

    const flowInteraction =
      action =>
      (target, properties, ...extra) =>
        Segment.event(`${flowName} ${target} ${action}`, { category: 'Flow Interaction', ...properties }, ...extra);

    return {
      loading: flowEvent('Loading'),
      start: flowEvent('User Started'),
      complete: flowEvent('User Completed'),
      click: flowInteraction('Clicked'),
      step: stepName => {
        const stepEvent = (eventName, properties, ...extra) =>
          Segment.event(
            `${flowName} ${stepName} ${eventName}`,
            { category: 'Flow Step', step: stepName, ...properties },
            ...extra
          );

        return {
          view: (properties, ...extra) =>
            stepEvent('Step Viewed', { previous: setCurrentStep(stepName), ...properties }, ...extra),
          complete: (...extra) => stepEvent('Step Completed', ...extra),
          error: (...extra) => stepEvent('Step Failed', ...extra),
          event: stepEvent,
        };
      },
    };
  }

  static init() {
    if (!Object.isFrozen(Segment)) {
      // Initialize the interface with queue methods.
      const INTERFACE = { ...mapMethods(methodName => queue.bind(null, methodName)) };

      // Expose all supported interface methods on the Segment class.
      Object.defineProperties(
        Segment,
        mapMethods(methodName => ({
          // Make sure that we can swap out the underlying methods.
          get() {
            return INTERFACE[methodName];
          },
        }))
      );

      const pollRealSegment = (attempt = 1) => {
        if (Segment.hasLoaded()) {
          // Replace with real methods.
          Object.assign(
            INTERFACE,
            mapMethods(methodName => window.analytics[methodName])
          );

          // Configure integrations so we only send events to Sprig (Actions) if the user is logged in.
          configureSegmentIntegrations();

          // Run through the queue & clear it.
          eventQueue.forEach(([method, args]) => Segment[method](...args));
          eventQueue.clear();
        } else if (attempt > MAX_RETRIES) {
          // Replace with no-op methods & clear the queue.
          Object.assign(
            INTERFACE,
            mapMethods(() => noop)
          );
          eventQueue.clear();
        } else {
          // Retry.
          setTimeout(pollRealSegment, RETRY_INTERVAL, attempt + 1);
        }
      };

      pollRealSegment();
    }

    return Segment;
  }
}

export default Object.freeze(Segment.init());
